import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useSidebar } from '../SidebarContext';

const LoanEMICalculator = () => {
    const getStoredValue = (key, defaultValue) => {
        const storedValue = sessionStorage.getItem(key);
        return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    };
    const { isSidebarOpen } = useSidebar();
    const [loanAmount, setLoanAmount] = useState(() => getStoredValue("loanAmount", 127588));
    const [interestRate, setInterestRate] = useState(() => getStoredValue("interestRate", 19.5));
    const [loanTenure, setLoanTenure] = useState(() => getStoredValue("loanTenure", 360));
    const navigate = useNavigate();

    const isValid = !isNaN(loanAmount) && !isNaN(interestRate) && !isNaN(loanTenure) && loanAmount > 0 && interestRate > 0 && loanTenure > 0;

    const r = interestRate / 12 / 100;
    const n = loanTenure;
    const EMI = isValid
        ? (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
        : 0;
    const totalPayment = isValid ? EMI * n : 0;
    const totalInterest = isValid ? totalPayment - loanAmount : 0;

    const principal = loanAmount;
    const total = totalPayment;
    const principalPercentage = isValid ? (principal / total) * 100 : 0;
    const interest = isValid ? total - principal : 0;
    const interestPercentage = isValid ? 100 - principalPercentage : 0;

    const [selected, setSelected] = useState("principal");
    const circleRef = useRef(null);

    useEffect(() => sessionStorage.setItem("loanAmount", JSON.stringify(loanAmount)), [loanAmount]);
    useEffect(() => sessionStorage.setItem("interestRate", JSON.stringify(interestRate)), [interestRate]);
    useEffect(() => sessionStorage.setItem("loanTenure", JSON.stringify(loanTenure)), [loanTenure]);

    const handleClick = (event) => {
        if (!circleRef.current) return;

        const rect = circleRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        const angle = (Math.atan2(y, x) * 180) / Math.PI + 90;
        const normalizedAngle = angle < 0 ? 360 + angle : angle;

        if (normalizedAngle <= (principalPercentage / 100) * 360) {
            setSelected("principal");
        } else {
            setSelected("interest");
        }
    };

    const formatINR = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const handleInputChange = (setter, key) => (e) => {
        let value = e.target.value;
        if (value === "") {
            setter("");
        } else {
            value = value.replace(/^0+/, ""); // Remove leading zeros
            setter(Number(value));
        }
    };

    const handleBlur = (setter, defaultValue) => () => {
        setter((prev) => (prev === "" ? defaultValue : prev));
    };

    return (
        <>
            <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="oms-header">
                    <h1 className="dashboard-title">EMI Calculator</h1>
                </div>
                <div className="loan-calculator-container-smco">
                    {/* Left Section - Input Controls */}
                    <div className="loan-calculator-input-section-smco">
                        <label>Loan Amount:</label>
                        <input
                            type="number"
                            value={loanAmount}
                            onChange={handleInputChange(setLoanAmount, "loanAmount")}
                            onBlur={handleBlur(setLoanAmount, 1000)}
                        />
                        <Slider
                            min={10000}
                            max={100000000}
                            value={loanAmount}
                            onChange={setLoanAmount}
                            trackStyle={{ backgroundColor: "#1a73e8" }}
                            handleStyle={{ borderColor: "#1a73e8" }}
                        />

                        <label>Interest Rate (%):</label>
                        <input
                            type="number"
                            value={interestRate}
                            onChange={handleInputChange(setInterestRate, "interestRate")}
                            onBlur={handleBlur(setInterestRate, 1)}
                        />
                        <Slider
                            min={1}
                            max={100}
                            step={0.1}
                            value={interestRate}
                            onChange={setInterestRate}
                            trackStyle={{ backgroundColor: "#1a73e8" }}
                            handleStyle={{ borderColor: "#1a73e8" }}
                        />

                        <label>Loan Tenure (Months):</label>
                        <input
                            type="number"
                            value={loanTenure}
                            onChange={handleInputChange(setLoanTenure, "loanTenure")}
                            onBlur={handleBlur(setLoanTenure, 12)}
                        />
                        <Slider
                            min={12}
                            max={480}
                            step={12}
                            value={loanTenure}
                            onChange={setLoanTenure}
                            trackStyle={{ backgroundColor: "#1a73e8" }}
                            handleStyle={{ borderColor: "#1a73e8" }}
                        />
                    </div>

                    {/* Middle Section - EMI Details */}
                    <div className="loan-calculator-emi-details-smco">
                        <h2>Your EMI Details</h2>
                        <p><div>Loan EMI:</div> <div><strong>{formatINR(EMI || 0)}</strong></div></p>
                        <p><div>Total Interest Payable:</div> <div><strong>{formatINR(totalInterest || 0)}</strong></div></p>
                        <p><div>Total Payment:</div> <div><strong>{formatINR(totalPayment || 0)}</strong></div></p>
                    </div>

                    {/* Right Section - Pie Chart */}
                    <div className="loan-calculator-chart-section-smco">
                        <div className="loan-calculator-chart-container-smco">
                            <div
                                ref={circleRef}
                                className="loan-calculator-circle-smco"
                                onClick={handleClick}
                                style={{
                                    background: `conic-gradient(
                ${selected === "principal" ? "#777684" : "rgba(119, 118, 132, 0.3)"} 0% ${principalPercentage}%, 
                ${selected === "interest" ? "#1a73e8" : "rgba(26, 115, 232, 0.3)"} ${principalPercentage}% 100%
            )`,
                                    transition: "background 0.3s ease",
                                    cursor: "pointer",
                                }}
                            >
                                <div className="loan-calculator-circle-inner-smco">
                                    <span className="loan-calculator-label-smco">
                                        {selected === "principal" ? "Principal" : "Interest"}
                                    </span>
                                    <span className="loan-calculator-value-smco">
                                        {(selected === "principal"
                                            ? principal
                                            : interest)?.toLocaleString() || "0"}
                                    </span>
                                </div>
                            </div>
                            <div className="loan-calculator-legend-smco">
                                <div
                                    onClick={() => setSelected("principal")}
                                    style={{
                                        cursor: "pointer",
                                        fontWeight: selected === "principal" ? "bold" : "normal",
                                    }}
                                >
                                    <span className="loan-calculator-box-smco loan-calculator-principal-smco"></span> Principal Amount
                                </div>
                                <div
                                    onClick={() => setSelected("interest")}
                                    style={{
                                        cursor: "pointer",
                                        fontWeight: selected === "interest" ? "bold" : "normal",
                                    }}
                                >
                                    <span className="loan-calculator-box-smco loan-calculator-interest-smco"></span> Interest Amount
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LoanEMICalculator;
